import { z } from 'zod';

/**
 * Automation tasks that stopped moving.
 *
 * The engine drives payment reminders, device locks and unlocks, and contract
 * completion off one claim table. When an action exhausts its attempts the row
 * is deliberately *not* reopened — reopening on failure would turn one provider
 * outage across a thousand contracts into hundreds of thousands of rows
 * retrying a dead endpoint. The cost of that is a device that failed to lock
 * stays unlocked, and a customer who has paid stays locked out, until someone
 * looks. This screen is the looking.
 *
 * Zod enums rather than TypeScript enums, per the project convention: one
 * declaration gives both the runtime values and the inferred union.
 */

/**
 * Which class of stuck row to list. Not a row `status` — both classes are a
 * status *plus* a second condition, and they mean different things to whoever
 * is reading them.
 */
export const AutomationTaskStateSchema = z.enum(['exhausted', 'stalled']);

export const AutomationTaskStatusSchema = z.enum([
  'pending',
  'claimed',
  'succeeded',
  'failed',
]);

/**
 * Mirrors `AutomationAction` in offerings-service. Kept closed because every
 * member here needs a label — a value we have not shipped a label for renders
 * through {@link actionLabel}'s fallback rather than as a blank cell, so a
 * backend-first release degrades to an ugly row, never an empty one.
 */
export const AutomationActionSchema = z.enum([
  'reminder_3day',
  'reminder_1day',
  'reminder_due_today',
  'overdue_warning',
  'overdue_partial_lock',
  'overdue_full_lock',
  'overdue_defaulted',
  'device_lock',
  'device_unlock',
  'contract_completion',
]);

export const AutomationTaskSchema = z.object({
  id: z.string(),
  /** The contract's external UUID — what /contracts/$contractId expects. */
  contractId: z.string(),
  /** 0 for actions that belong to the contract as a whole, not an installment. */
  installmentNumber: z.number(),
  action: AutomationActionSchema,
  status: AutomationTaskStatusSchema,
  attemptCount: z.number(),
  maxAttempts: z.number(),
  lastError: z.string().nullable(),
  scheduledFor: z.string(),
  /** Null on an exhausted row; on a stalled one, how long it has been stuck. */
  claimedAt: z.string().nullable(),
  lastModifiedAt: z.string(),
});

export const AutomationTaskFiltersSchema = z.object({
  state: AutomationTaskStateSchema,
  skip: z.number().optional(),
  limit: z.number().optional(),
});

export type AutomationTaskState = z.infer<typeof AutomationTaskStateSchema>;
export type AutomationTaskStatus = z.infer<typeof AutomationTaskStatusSchema>;
export type AutomationAction = z.infer<typeof AutomationActionSchema>;
export type AutomationTask = z.infer<typeof AutomationTaskSchema>;
export type AutomationTaskFilters = z.infer<typeof AutomationTaskFiltersSchema>;

/** Contract-level actions are pinned to this installment number by definition. */
export const CONTRACT_LEVEL_INSTALLMENT = 0;

export const ACTION_LABELS: Record<AutomationAction, string> = {
  reminder_3day: 'Reminder (3 days before)',
  reminder_1day: 'Reminder (1 day before)',
  reminder_due_today: 'Reminder (due today)',
  overdue_warning: 'Overdue notice — warning',
  overdue_partial_lock: 'Overdue notice — partial lock',
  overdue_full_lock: 'Overdue notice — full lock',
  overdue_defaulted: 'Overdue notice — defaulted',
  device_lock: 'Lock device',
  device_unlock: 'Unlock device',
  contract_completion: 'Complete contract',
};

/**
 * A readable name for an action, including one this build has never heard of.
 */
export function actionLabel(action: string): string {
  const known = ACTION_LABELS[action as AutomationAction];
  if (known) return known;
  const spaced = action.split('_').join(' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/**
 * Actions that touch a handset. These are the rows where being stuck is felt by
 * a customer rather than only by the operator: a failed `device_unlock` is
 * someone who has paid and cannot use their phone.
 */
export const DEVICE_ACTIONS: readonly string[] = [
  'device_lock',
  'device_unlock',
] as const;
